import {
  append,
  find,
  head,
  isEmpty,
  isNil,
  keys,
  mapObjIndexed,
  merge,
  not,
  partition,
  pathOr,
  pluck,
  prop,
  reduce,
  reject,
  uniqBy,
  values,
} from "ramda";
import { isUserPosOnly } from "helpers/shared/users";

import type {
  IAppUser,
  IAppUserApplications,
  IPOSUserInfo,
  IUserInfo,
} from "types/user/app_user";
import type IPosUser from "types/user/pos_user";

const APP_PRIORITY_FOR_NAME: string[] = [
  "hq",
  "analytics",
  "breadcrumb",
  "inventory",
];

interface IAppUserName {
  order: number;
  firstName: string;
  lastName: string;
}

function extractFirstAndLastByApp(
  appLocations: any,
  key: string
): IAppUserName | undefined {
  const appPriority: number = APP_PRIORITY_FOR_NAME.indexOf(key);
  const firstName: string | undefined = find(
    (name: string) => not(isEmpty(name)),
    pluck("firstName", appLocations)
  );
  const lastName: string | undefined = find(
    (name: string) => not(isEmpty(name)),
    pluck("lastName", appLocations)
  );
  if (firstName && lastName) {
    return {
      order: appPriority,
      firstName,
      lastName,
    };
  }
}

function transformForUsersList(
  meta: IAppUserApplications,
  key: string
): IAppUser {
  const firstAndLastByApp: IAppUserName[] = reject(
    isNil,
    values(mapObjIndexed(extractFirstAndLastByApp, meta))
  ) as IAppUserName[];

  const validFirstAndLastByApp: IAppUserName[] = firstAndLastByApp.sort(
    (a: IAppUserName, b: IAppUserName) => a.order - b.order
  );
  const prioritizedName: IAppUserName | undefined = head(
    validFirstAndLastByApp
  );

  return {
    id: pathOr(null, ["hq", 0, "id"], meta),
    email: key,
    firstName: prioritizedName ? prioritizedName.firstName : "",
    lastName: prioritizedName ? prioritizedName.lastName : "",
    applications: meta,
  };
}

function getPosUserRoles(user: IPosUser) {
  return pluck<"roleId", string>("roleId", user.posUserRoles);
}

function reconcileUsers(
  hq: IUnreconciledUser[],
  analytics: IUnreconciledAnalyticsUser[],
  inventory: IUnreconciledInventoryCustomers,
  ...bcUsersByStore: IUnreconciledBreadcrumbStore[]
): { users: IAppUser[]; posOnlyUsers: IPOSUserInfo[] } {
  // HQ Service users
  let users: IReconciledUsers = keyUsers("email", "id", "hq", {}, hq);

  // Analytics users
  users = keyUsers("email", "uuid", "analytics", users, analytics, (user) => ({
    roles: [user.roleId],
  }));

  // Breadcrumb users per store
  const posOnlyUsers: IPOSUserInfo[] = [];
  users = bcUsersByStore.reduce((acc, bcLocation) => {
    const [posOnly, usersWithEmail] = partition(
      isUserPosOnly,
      bcLocation.users.posUsers
    );

    // Accumulate POS only users separately
    reduce(
      (accPosOnly: IPOSUserInfo[], thisUser: IPosUser) => {
        const transformed: IPOSUserInfo = {
          id: thisUser.id,
          firstName: thisUser.firstName,
          lastName: thisUser.lastName,
          pincode: thisUser.pincode || undefined,
          storeKey: bcLocation.storeKey,
          roles: getPosUserRoles(thisUser),
        };
        accPosOnly.push(transformed);
        return accPosOnly;
      },
      posOnlyUsers,
      posOnly
    );

    return keyUsers(
      "username",
      "id",
      "breadcrumb",
      acc,
      usersWithEmail,
      (user: IPosUser) => ({
        pincode: user.pincode,
        storeKey: bcLocation.storeKey,
        roles: getPosUserRoles(user),
      })
    );
  }, users);

  // Inventory users per store
  users = keyUsersForInventory(users, inventory);

  return {
    users: values(mapObjIndexed(transformForUsersList, users)),
    posOnlyUsers: uniqBy(prop("id"), posOnlyUsers),
  };
}

function keyUsersForInventory(
  reconciledUsers: IReconciledUsers,
  customersWithUsers: IUnreconciledInventoryCustomers
): IReconciledUsers {
  return keys(customersWithUsers.customers).reduce((acc, customerId) => {
    return keyUsers(
      "email",
      "id",
      "inventory",
      acc,
      customersWithUsers.customers[customerId],
      () => ({
        storeKey: customerId,
      })
    );
  }, reconciledUsers);
}

interface IMergeOpts {
  [key: string]: any;
}

// TODO: Create similar interfaces for each backend so that the mock data can
//       be validated and give us confidence in the API responses being typed
//       properly.
interface IUnreconciledAnalyticsUser {
  uuid: string;
  email: string;
  firstName: string;
  hierarchy: {
    allBrands: boolean;
    brands: {
      allLocations: boolean;
      locations: {
        friendlyName: string;
        name: string;
        prettyUrl: string;
        salesforceId: string;
      }[];
      name: string;
      prettyUrl: string;
      salesforceId: string;
    }[];
  };
  lastActiveAt: string;
  lastLoginAt: string;
  lastName: string;
  roleId: string;
  roleName: string;
}

// this is purposely vague
interface IUnreconciledUser {
  firstName?: string;
  lastName?: string;
  username?: string;
  [key: string]: any;
}

interface IReconciledUsers {
  [email: string]: any;
}

interface IUnreconciledInventoryUser {
  id: string;
  email: string;
  name: string;
  username: string;
}

interface IUnreconciledInventoryCustomers {
  customers: {
    [customerId: string]: IUnreconciledInventoryUser[];
  };
}

interface IUnreconciledBreadcrumbStore {
  storeKey: string;
  users: IUnreconciledBreadcrumbUsers;
}

interface IUnreconciledBreadcrumbUsers {
  posUsers: IPosUser[];
}

function keyUsers<T>(
  joinKey: string, // this is the key to join users across systems
  idKey: string, // this is the system-specific unique id needed to navigate to the user's edit page for a given system
  namedSystemKey: string, // this is the name of the system for this user to nest under
  accumulator: IReconciledUsers,
  users: T[],
  makeUserProperties?: (user: T) => IMergeOpts // make addtl properties to merge ie. store key
): IReconciledUsers {
  // @ts-expect-error this type error is too complicated to fix
  return users.reduce((acc: IReconciledUsers, user: IUnreconciledUser) => {
    let joinValue = "";
    if (typeof user[joinKey] === "string") {
      joinValue = user[joinKey].toLocaleLowerCase();
    }

    const existingUser: IReconciledUsers = acc[joinValue];

    let firstName = user.firstName;
    if (!firstName && user.name) {
      firstName = user.name.split(" ")[0];
    }
    let lastName = user.lastName;
    if (!lastName && user.name) {
      lastName = user.name.split(" ")[1];
    }

    const mergeOpts =
      typeof makeUserProperties === "function"
        ? makeUserProperties(user as T)
        : {};
    const userToSystemReference: IUserInfo = {
      id: String(user[idKey]),
      firstName,
      lastName,
      roles: [],
      ...mergeOpts,
    };

    const namedSystemValue = existingUser && existingUser[namedSystemKey];
    const userSystemReferences = namedSystemValue
      ? append(userToSystemReference, namedSystemValue)
      : [userToSystemReference];

    const updatedUser = merge(acc[joinValue] || {}, {
      [namedSystemKey]: userSystemReferences,
    });

    const reducedUsers = merge(acc, {
      [joinValue]: updatedUser,
    });

    return reducedUsers;
  }, accumulator);
}

export {
  IUnreconciledUser,
  IUnreconciledBreadcrumbUsers,
  IUnreconciledBreadcrumbStore,
  IUnreconciledAnalyticsUser,
  IUnreconciledInventoryCustomers,
  keyUsers,
  reconcileUsers,
};
